import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl.replace(/\/$/, '');

  get<T>(endpoint: string, params?: QueryParams): Observable<T> {
    return this.http.get<T>(this.buildUrl(endpoint), {
      params: this.buildParams(params),
    });
  }

  post<T>(endpoint: string, payload: unknown): Observable<T> {
    return this.http.post<T>(this.buildUrl(endpoint), payload);
  }

  postText(endpoint: string, payload: unknown): Observable<string> {
    return this.http.post(this.buildUrl(endpoint), payload, {
      responseType: 'text',
    });
  }

  put<T>(endpoint: string, payload: unknown): Observable<T> {
    return this.http.put<T>(this.buildUrl(endpoint), payload);
  }

  putText(endpoint: string, payload: unknown): Observable<string> {
    return this.http.put(this.buildUrl(endpoint), payload, {
      responseType: 'text',
    });
  }

  delete<T>(endpoint: string): Observable<T> {
    return this.http.delete<T>(this.buildUrl(endpoint));
  }

  deleteText(endpoint: string): Observable<string> {
    return this.http.delete(this.buildUrl(endpoint), {
      responseType: 'text',
    });
  }

  deleteTextWithBody(endpoint: string, payload: unknown): Observable<string> {
    return this.http.delete(this.buildUrl(endpoint), {
      body: payload,
      responseType: 'text',
    });
  }

  private buildUrl(endpoint: string): string {
    const normalizedEndpoint = endpoint.replace(/^\//, '');
    return `${this.apiBaseUrl}/${normalizedEndpoint}`;
  }

  private buildParams(params?: QueryParams): HttpParams | undefined {
    if (!params) {
      return undefined;
    }

    return Object.entries(params).reduce((httpParams, [key, value]) => {
      if (value === null || value === undefined || value === '') {
        return httpParams;
      }

      return httpParams.set(key, String(value));
    }, new HttpParams());
  }
}

type QueryValue = string | number | boolean | null | undefined;
type QueryParams = Record<string, QueryValue>;
